// src/pages/home-restaurant-discovery/restaurantUrl.js
import { resolveCartTimeZone, wallClockToIso } from '@/utils/timezoneUtils';

/**
 * Builds the /restaurant/:id URL that discovery hands off to the menu page.
 *
 * Shared by the restaurant card and the menu-preview "Order from restaurant"
 * handler so both carry the same fulfillment intent. The page is opened in a
 * new tab, so location.state never arrives — everything the detail page needs
 * (including the ASAP flag and the cart title) must ride the URL.
 */
export function buildRestaurantUrl({
  restaurantId,
  restaurant = null,
  selectedService,
  fulfillment,
  mealType,
  initialCartTitle,
}) {
  const id = restaurantId ?? '';
  const params = new URLSearchParams();
  if (selectedService) params.set('service', selectedService);
  if (fulfillment?.date) params.set('date', fulfillment.date);
  if (fulfillment?.time) params.set('time', fulfillment.time);
  // The picked wall clock is the restaurant's (then the address's), never the
  // coach's device. With neither zone known (an assistant pickup link carries
  // no coords) the instant is left out: the menu page reads date and time on
  // the restaurant's clock, and a device-clock guess (6 PM in New York for a
  // California store) could mislead the next load.
  const timeZone = fulfillment?.date && fulfillment?.time
    ? resolveCartTimeZone(restaurant, fulfillment?.coords)
    : null;
  if (timeZone) {
    const whenIso = wallClockToIso(fulfillment.date, fulfillment.time, timeZone);
    if (whenIso) params.set('whenISO', whenIso);
  }
  if (fulfillment?.address) {
    const key = selectedService === 'pickup' ? 'pickup_address' : 'delivery_address';
    params.set(key, fulfillment.address);
  }
  if (fulfillment?.coords?.lat != null && fulfillment?.coords?.lng != null) {
    params.set('lat', String(fulfillment.coords.lat));
    params.set('lng', String(fulfillment.coords.lng));
  }
  // Carries the "soonest" intent to the menu page, where it re-resolves
  // against that restaurant's own prep time.
  if (fulfillment?.asap === true) params.set('asap', '1');
  if (typeof fulfillment?.autoSubmit === 'boolean') {
    params.set('autoSubmit', fulfillment.autoSubmit ? '1' : '0');
  }
  const effectiveMealType = (mealType || fulfillment?.mealType || '').trim();
  if (effectiveMealType) params.set('mealType', effectiveMealType);
  const title = (initialCartTitle ?? '').trim();
  if (title) params.set('title', title);

  const query = params.toString();
  return query ? `/restaurant/${id}?${query}` : `/restaurant/${id}`;
}
